import { environment } from '../../../environment';
import { MktApiKey, MktScope } from '../../../services/marketing/marketing';

// What the AI connection section says about keys, and the setup it hands out.

/** Settings → AI connection; the Review page links here. */
export const AI_CONNECTION_ANCHOR = 'ai-connection';
/** The launch limit field inside AI connection. */
export const LAUNCH_LIMIT_ID = 'ai-launch-limit';

/** What the backend publishes; the OAuth apps (claude.ai, ChatGPT) only need the MCP address. */
export const MCP_URL = `${environment.v2Url}/marketing-api/mcp`;
export const OPENAPI_URL = `${environment.v2Url}/marketing-api/openapi.json`;
/** Where REST tools live when no key response has said (`api_url` from a new key wins). */
export const API_URL = `${environment.v2Url}/marketing-api`;
/** Stands in for the secret in guides shown without a new key. */
export const KEY_PLACEHOLDER = 'YOUR_KEY';

export const DEFAULT_LAUNCH_LIMIT = 500;

export interface ScopeInfo {
    scope: MktScope;
    label: string;
    /** On key rows. */
    chip: string;
    help: string;
    always?: boolean;
    /** Other scopes this one includes: ticking it ticks them. */
    includes?: MktScope[];
    tone?: 'amber' | 'red';
}

/** In the order the "New key" dialog and the consent page list them. */
export const SCOPES: ScopeInfo[] = [
    { scope: 'read', label: 'Read', chip: 'Read', always: true, help: 'See the workspace, audiences, emails, campaigns, flows, links, signals and results. Always included.' },
    { scope: 'read:contacts', label: "See people's details", chip: "People's details", tone: 'amber', help: 'See individual people: names, email addresses, who is in a segment or flow, a person\'s activity.' },
    { scope: 'read:sales', label: 'See sales info', chip: 'Sales info', tone: 'amber', help: 'See CRM leads: journeys and stages, scores, owners, where they came from. Wallmates workspace only.' },
    { scope: 'draft', label: 'Draft', chip: 'Draft', help: 'Create and change its own segments, emails, campaigns, flows and tagged links, and define new signals.' },
    { scope: 'manage', label: 'Manage', chip: 'Manage', tone: 'amber', includes: ['draft'], help: 'Change or archive anything, including what people made; pause flows and campaigns. Includes draft.' },
    { scope: 'track', label: 'Record signals', chip: 'Record signals', help: 'Record signals for people (for example "viewed the pricing page"). Good alone for a server or Zapier.' },
    { scope: 'submit', label: 'Submit for review', chip: 'Submit for review', help: 'Hand drafts to the Review page.' },
    { scope: 'launch', label: 'Launch without review', chip: 'Launch without review', tone: 'red', help: 'Turn flows on and send campaigns without review, for at most the launch limit of people each time.' }
];

export interface ScopePreset { id: string; label: string; help: string; scopes: MktScope[]; warn?: boolean }

const OPERATOR: MktScope[] = ['read', 'read:contacts', 'read:sales', 'draft', 'manage', 'track', 'submit'];

export const PRESETS: ScopePreset[] = [
    { id: 'analyst', label: 'Analyst', help: 'Reads results, people and sales info. Changes nothing.', scopes: ['read', 'read:contacts', 'read:sales'] },
    { id: 'assistant', label: 'Assistant', help: 'Drafts and hands everything to the Review page.', scopes: ['read', 'draft', 'submit'] },
    { id: 'operator', label: 'Operator', help: 'Runs the day to day, but anything that sends still goes to review.', scopes: OPERATOR },
    { id: 'autopilot', label: 'Autopilot', help: 'Operator, and it can send and turn flows on by itself.', scopes: [...OPERATOR, 'launch'], warn: true },
    { id: 'recorder', label: 'Signal recorder', help: 'Records signals from a server, Zapier or a form. Nothing else.', scopes: ['read', 'track'] }
];

export const DEFAULT_SCOPES: MktScope[] = ['read', 'draft', 'submit'];

export const EXPIRY_DAYS = [30, 90, 180, 365];
export const DEFAULT_EXPIRY_DAYS = 90;

/** Keys this close to expiring get a warning on the list. */
const WARN_DAYS = 14;

// #region scopeInfo
export const scopeInfo = (s: string): ScopeInfo | undefined => SCOPES.find((x) => x.scope === s);

// #region scopeChip
export const scopeChip = (s: string): string => scopeInfo(s)?.chip ?? s;

// #region normaliseScopes
/** Always read, plus whatever a ticked scope includes; in the dialog's order. */
export const normaliseScopes = (scopes: MktScope[]): MktScope[] => {
    const set = new Set<MktScope>(['read', ...scopes]);
    for (const s of SCOPES) if (set.has(s.scope)) for (const i of s.includes ?? []) set.add(i);
    return SCOPES.map((s) => s.scope).filter((s) => set.has(s));
};

// #region presetFor
/** The preset these scopes match exactly, if any. */
export const presetFor = (scopes: MktScope[]): ScopePreset | null => {
    const key = normaliseScopes(scopes).join(',');
    return PRESETS.find((p) => normaliseScopes(p.scopes).join(',') === key) ?? null;
};

// #region launchLimitText
export const launchLimitText = (limit: number): string =>
    limit > 0 ? `${limit.toLocaleString('en-US')} people` : 'never (set to 0)';

// #region expiryState
/** Whether a key has expired, expires soon, or is fine; days left when it expires at all. */
export const expiryState = (key: MktApiKey, now: number = Date.now()): { state: 'none' | 'ok' | 'soon' | 'expired'; days: number | null } => {
    if (!key.expires_at) return { state: 'none', days: null };
    const t = new Date(key.expires_at).getTime();
    if (!Number.isFinite(t)) return { state: 'none', days: null };
    const days = Math.ceil((t - now) / 86400000);
    if (t <= now) return { state: 'expired', days: 0 };
    return { state: days < WARN_DAYS ? 'soon' : 'ok', days };
};

// #region guide snippets
export const claudeCodeCommand = (mcpUrl: string, secret: string): string =>
    `claude mcp add --transport http wallmates ${mcpUrl} --header "Authorization: Bearer ${secret}"`;

/** The block Cursor, VS Code and Windsurf read from mcp.json. */
export const mcpJson = (mcpUrl: string, secret: string): string => JSON.stringify({
    mcpServers: { wallmates: { url: mcpUrl, headers: { Authorization: `Bearer ${secret}` } } }
}, null, 2);

/** The OpenAI Responses API / Agents SDK tool entry. */
export const openAiTool = (mcpUrl: string, secret: string): string => JSON.stringify({
    type: 'mcp', server_label: 'wallmates', server_url: mcpUrl, headers: { Authorization: `Bearer ${secret}` }, require_approval: 'never'
}, null, 2);

/** One REST call that proves the key works. */
export const curlExample = (apiUrl: string, secret: string): string =>
    `curl -X POST ${apiUrl}/tools/get_workspace -H "Authorization: Bearer ${secret}" -H "Content-Type: application/json" -d '{}'`;
